import React from "react";
import { X } from "lucide-react";
import LinkPickerField from "components/Tickets/LinkPickerField";
import { buildIntakeSubmission } from "utilities/intakeSubmission";

/**
 * TemplateIntakeForm (SOUND-508) — renders an active template's `intakeFields`
 * as the structured create-ticket form. Field values lift to the modal as
 * `{ fieldKey: value }` (sent as `body.fields` — the backend validates +
 * instantiates the flow). Mirrors the backend intakeValueValidator:
 *   - `visibleWhen` show/hide against earlier answers (a hidden field is not
 *     rendered, not required, not submitted).
 *   - `gridCols` 1 = full width, 2 = half.
 *
 * Field types: text / textarea / number / date / boolean / select /
 * select-other (reveals a free-text input for the `otherKey`) / mid-ref +
 * company-ref (reuse the Phase 2 LinkPickerField with a selected-chip). Refs
 * store the entityId (sound_mid_id / companyId); a parallel `labels` map holds
 * the display label. `onChange(key, value, label?)`.
 */
export default function TemplateIntakeForm({
  fields = [],
  values = {},
  labels = {},
  errors = {},
  onChange,
  companyId,
}) {
  // SOUND-791: visibility as the server will judge it, off the payload sent.
  const { visibleKeys } = buildIntakeSubmission(fields, values);
  const visible = fields.filter((f) => f && f.key && visibleKeys.has(f.key));
  if (visible.length === 0) return null;

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      {visible.map((f) => {
        const span = f.gridCols === 2 ? "" : "sm:col-span-2";
        const err = errors[f.key];
        return (
          <div key={f.key} className={`flex flex-col text-sm ${span}`}>
            <span className="mb-1 font-medium text-foreground">
              {f.label || f.key}
              {f.required && <span className="text-destructive"> *</span>}
            </span>
            <FieldControl field={f} values={values} labels={labels} onChange={onChange} companyId={companyId} />
            {f.helpText && <span className="mt-1 text-xs text-muted-foreground">{f.helpText}</span>}
            {err && <span className="mt-1 text-xs text-destructive">{err}</span>}
          </div>
        );
      })}
    </div>
  );
}

function FieldControl({ field: f, values, labels, onChange, companyId }) {
  const val = values[f.key];
  const set = (v, label) => onChange(f.key, v, label);

  switch (f.type) {
    case "textarea":
      return (
        <textarea
          className="textarea min-h-[72px]"
          placeholder={f.placeholder || ""}
          value={val || ""}
          onChange={(e) => set(e.target.value)}
        />
      );
    case "number":
      return (
        <input
          type="number"
          className="input"
          placeholder={f.placeholder || ""}
          value={val ?? ""}
          onChange={(e) => set(e.target.value)}
        />
      );
    case "date":
      return <input type="date" className="input" value={val || ""} onChange={(e) => set(e.target.value)} />;
    case "boolean":
      return <YesNoControl field={f} value={val} onChange={set} />;
    case "select":
      return (
        <select className="select" value={val || ""} onChange={(e) => set(e.target.value)}>
          <option value="">Select…</option>
          {(f.options || []).map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      );
    case "select-other": {
      const isOther = String(val || "").toLowerCase() === "other";
      return (
        <div className="space-y-2">
          <select className="select" value={val || ""} onChange={(e) => set(e.target.value)}>
            <option value="">Select…</option>
            {(f.options || []).map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
          {isOther && f.otherKey && (
            <input
              type="text"
              className="input"
              placeholder="Please specify…"
              value={values[f.otherKey] || ""}
              onChange={(e) => onChange(f.otherKey, e.target.value)}
            />
          )}
        </div>
      );
    }
    case "mid-ref":
      return <RefField field={f} entityType="mid" label={labels[f.key]} value={val} onChange={onChange} companyId={companyId} />;
    case "company-ref":
      return <RefField field={f} entityType="company" label={labels[f.key]} value={val} onChange={onChange} />;
    default: // text
      return (
        <input
          type="text"
          className="input"
          placeholder={f.placeholder || ""}
          value={val || ""}
          onChange={(e) => set(e.target.value)}
        />
      );
  }
}

/**
 * Yes/No control for the `boolean` intake type (SOUND-573).
 *
 * This type is LABELLED "Yes / No" in the workflow builder but used to render as
 * a single checkbox labelled "Yes" — there was no "No" at all, and an untouched
 * checkbox (unanswered) was visually identical to a deliberate "No". A required
 * Yes/No field was therefore unsatisfiable by answering No: the user saw an empty
 * box, submitted, and got "Missing required field".
 *
 * Three states are now distinct, which is the whole point:
 *   undefined → unanswered (neither radio selected; fails a required field)
 *   true      → Yes
 *   false     → No  (a real answer, stored, passes a required field)
 *
 * The VALUE is a real boolean; "Yes"/"No" are presentation only. That is the
 * contract SOUND-546's conditional step-spawning compares against — a boolean
 * can't drift, whereas a select option's label is author-editable free text.
 */
function YesNoControl({ field, value, onChange }) {
  const name = `intake-bool-${field.key}`;
  const answered = value === true || value === false;
  return (
    <div
      role="radiogroup"
      aria-label={field.label || field.key}
      className="flex items-center gap-4 py-1.5"
    >
      {[
        { v: true, label: "Yes" },
        { v: false, label: "No" },
      ].map((opt) => (
        <label
          key={opt.label}
          className="flex cursor-pointer items-center gap-2 text-sm text-foreground"
        >
          <input
            type="radio"
            name={name}
            checked={value === opt.v}
            onChange={() => onChange(opt.v)}
            className="h-4 w-4 border-border text-brand focus:ring-brand/40"
          />
          {opt.label}
        </label>
      ))}
      {/* Optional fields need a way BACK to unanswered — otherwise the first
          click is irreversible and "No" becomes indistinguishable from a
          mis-click the user can't undo. Required fields have no valid unanswered
          state, so no control is offered. */}
      {answered && !field.required && (
        <button
          type="button"
          onClick={() => onChange(undefined)}
          className="text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
        >
          Clear
        </button>
      )}
    </div>
  );
}

// CRM-entity reference: a selected chip once picked (with clear), else the
// debounced LinkPickerField. mid-ref scopes to the ticket's company; company-ref
// is cross-company (LinkPickerField's "company" case ignores companyId).
function RefField({ field, entityType, value, label, onChange, companyId }) {
  if (value) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-border bg-muted/30 px-3 py-2">
        <span className="min-w-0 flex-1 truncate text-sm text-foreground">{label || value}</span>
        <button
          type="button"
          onClick={() => onChange(field.key, "", "")}
          className="btn btn-ghost h-6 w-6 p-0"
          aria-label="Clear selection"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }
  return (
    <LinkPickerField
      entityType={entityType}
      companyId={companyId}
      onSelect={({ entityId, label: lbl }) => onChange(field.key, entityId, lbl)}
      placeholder={`Search ${entityType === "mid" ? "MID" : "company"}…`}
    />
  );
}
